import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { supportedLanguages, LANGUAGE_KEY, type LanguageCode } from '@/i18n'

interface LanguageContextType {
  currentLanguage: LanguageCode
  changeLanguage: (lang: LanguageCode) => void
  supportedLanguages: typeof supportedLanguages
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(
    (localStorage.getItem(LANGUAGE_KEY) as LanguageCode) || 'en'
  )

  // Sync with i18n on mount
  useEffect(() => {
    const storedLang = localStorage.getItem(LANGUAGE_KEY) as LanguageCode
    if (storedLang && supportedLanguages.some(l => l.code === storedLang)) {
      i18n.changeLanguage(storedLang)
      setCurrentLanguage(storedLang)
    }
  }, [i18n])

  const changeLanguage = useCallback((lang: LanguageCode) => {
    i18n.changeLanguage(lang)
    localStorage.setItem(LANGUAGE_KEY, lang)
    setCurrentLanguage(lang)
    // Update document language attribute for accessibility
    document.documentElement.lang = lang
  }, [i18n])

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    supportedLanguages,
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

// Export for convenience
export { supportedLanguages } from '@/i18n'
export type { LanguageCode } from '@/i18n'

